import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageHeader } from "@/components/ui/page-header";
import { openFinanceApi } from "@/services/api";
import { formatCurrency, formatDate } from "@/lib/utils";
import {
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Building2,
  Wallet,
  TrendingUp,
  ExternalLink,
  Trash2,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";

interface OpenFinanceLink {
  id: string;
  companyId: string;
  externalId: string;
  iniciadorLinkId: string | null;
  status: string;
  statusMessage: string | null;
  participantId: string | null;
  participantName: string | null;
  participantLogo: string | null;
  permissions: string[] | null;
  interfaceUrl: string | null;
  consentRedirectUrl: string | null;
  createdAt: string;
  authorizedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  lastSyncAt: string | null;
}

interface OpenFinanceAccount {
  id: string;
  linkId: string;
  companyId: string;
  accountId: string;
  brandName: string | null;
  companyCnpj: string | null;
  accountType: string | null;
  compeCode: string | null;
  branchCode: string | null;
  accountNumber: string | null;
  checkDigit: string | null;
  cachedAvailableBalance: string | null;
  cachedBlockedBalance: string | null;
  cachedBalanceAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface OpenFinanceSummary {
  totalAvailable: string;
  totalBlocked: string;
  accountsCount: number;
  activeLinksCount: number;
  lastSyncAt: string | null;
}

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "success" | "warning" | "destructive"; icon: React.ReactNode }> = {
  PENDING: { label: "Pendente", variant: "secondary", icon: <Clock className="w-3 h-3" /> },
  AWAITING_AUTHORISATION: { label: "Aguardando Autorização", variant: "warning", icon: <Clock className="w-3 h-3" /> },
  AUTHORISED: { label: "Autorizado", variant: "success", icon: <CheckCircle className="w-3 h-3" /> },
  REVOKED: { label: "Revogado", variant: "destructive", icon: <XCircle className="w-3 h-3" /> },
  EXPIRED: { label: "Expirado", variant: "destructive", icon: <AlertCircle className="w-3 h-3" /> },
  ERROR: { label: "Erro", variant: "destructive", icon: <AlertCircle className="w-3 h-3" /> },
};

export function CompanyOpenFinancePage() {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [summary, setSummary] = useState<OpenFinanceSummary | null>(null);
  const [links, setLinks] = useState<OpenFinanceLink[]>([]);
  const [accounts, setAccounts] = useState<OpenFinanceAccount[]>([]);

  // Dialog states
  const [linkToRevoke, setLinkToRevoke] = useState<OpenFinanceLink | null>(null);
  const [revoking, setRevoking] = useState(false);

  async function loadData() {
    setLoading(true);
    try {
      const [summaryData, linksData, accountsData] = await Promise.all([
        openFinanceApi.getSummary(),
        openFinanceApi.getLinks(),
        openFinanceApi.getAccounts(),
      ]);
      setSummary(summaryData);
      setLinks(linksData);
      setAccounts(accountsData);
    } catch (error) {
      console.error("Erro ao carregar dados Open Finance:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  async function handleCreateLink() {
    setCreating(true);
    try {
      const result = await openFinanceApi.createLink();
      // Abre a URL de autorização em nova aba
      if (result.authorizationUrl) {
        window.open(result.authorizationUrl, "_blank");
      }
      // Recarrega os dados
      await loadData();
    } catch (error) {
      console.error("Erro ao criar link:", error);
    } finally {
      setCreating(false);
    }
  }

  async function handleRevokeLink() {
    if (!linkToRevoke) return;

    setRevoking(true);
    try {
      await openFinanceApi.revokeLink(linkToRevoke.id);
      setLinkToRevoke(null);
      await loadData();
    } catch (error) {
      console.error("Erro ao revogar link:", error);
    } finally {
      setRevoking(false);
    }
  }

  async function handleRefreshAccount(accountId: string) {
    try {
      await openFinanceApi.refreshAccountBalance(accountId);
      await loadData();
    } catch (error) {
      console.error("Erro ao atualizar saldo:", error);
    }
  }

  // Calcula rendimento potencial
  const totalAvailable = parseFloat(summary?.totalAvailable || "0");
  const potentialMonthlyYield = totalAvailable * 0.0095; // ~0.95% ao mês
  const potentialAnnualYield = totalAvailable * 0.1265; // ~12.65% ao ano

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Link2 className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Open Finance"
          description="Conecte suas contas bancárias e descubra oportunidades"
        />
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Link2 className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Open Finance"
        description="Conecte suas contas bancárias e descubra oportunidades"
        stats={[
          { value: summary?.accountsCount || 0, label: "Contas" },
          { value: summary?.activeLinksCount || 0, label: "Bancos" },
        ]}
        actions={
          <Button
            onClick={handleCreateLink}
            disabled={creating}
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            {creating ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Conectar Banco
          </Button>
        }
      />

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Saldo Disponível
            </CardTitle>
            <Wallet className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-xl sm:text-2xl font-bold">
              {formatCurrency(parseFloat(summary?.totalAvailable || "0"))}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Em {summary?.accountsCount || 0} conta(s)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Saldo Bloqueado
            </CardTitle>
            <AlertCircle className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-xl sm:text-2xl font-bold">
              {formatCurrency(parseFloat(summary?.totalBlocked || "0"))}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Valores não disponíveis
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Rendimento Potencial
            </CardTitle>
            <TrendingUp className="w-4 h-4 text-success" />
          </CardHeader>
          <CardContent>
            <p className="text-xl sm:text-2xl font-bold text-success">
              +{formatCurrency(potentialMonthlyYield)}/mês
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              ~{formatCurrency(potentialAnnualYield)}/ano (95% CDI)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Última Sincronização
            </CardTitle>
            <RefreshCw className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-xl sm:text-2xl font-bold">
              {summary?.lastSyncAt ? formatDate(summary.lastSyncAt) : "—"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {summary?.lastSyncAt ? "Dados atualizados" : "Nenhuma sincronização"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Oportunidade CTA */}
      {totalAvailable > 0 && (
        <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Faça seu dinheiro render!</h3>
                  <p className="text-sm text-muted-foreground">
                    Você tem <span className="font-semibold text-foreground">{formatCurrency(totalAvailable)}</span> parado em suas contas.
                    Deposite no BRLD e ganhe <span className="font-semibold text-success">+{formatCurrency(potentialMonthlyYield)}/mês</span>.
                  </p>
                </div>
              </div>
              <Button className="w-full sm:w-auto">
                Depositar Agora
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bancos conectados */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Bancos Conectados
          </CardTitle>
        </CardHeader>
        <CardContent>
          {links.length === 0 ? (
            <div className="text-center py-8">
              <Link2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground mb-2">Nenhum banco conectado ainda</p>
              <p className="text-sm text-muted-foreground mb-4">
                Conecte seus bancos para visualizar seus saldos e descobrir oportunidades de investimento.
              </p>
              <Button onClick={handleCreateLink} disabled={creating}>
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Conectar Banco
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {links.map((link) => {
                const statusConfig = STATUS_CONFIG[link.status] || STATUS_CONFIG.PENDING;
                const linkAccounts = accounts.filter((a) => a.linkId === link.id);

                return (
                  <div key={link.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {link.participantLogo ? (
                          <img
                            src={link.participantLogo}
                            alt={link.participantName || "Banco"}
                            className="w-10 h-10 rounded-lg object-contain bg-white"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">
                            {link.participantName || "Banco não identificado"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Conectado em {formatDate(link.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={statusConfig.variant} className="gap-1">
                          {statusConfig.icon}
                          {statusConfig.label}
                        </Badge>
                        {link.status === "AUTHORISED" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setLinkToRevoke(link)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                        {link.status === "AWAITING_AUTHORISATION" && link.interfaceUrl && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(link.interfaceUrl!, "_blank")}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Autorizar
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Contas do banco */}
                    {linkAccounts.length > 0 && (
                      <div className="border-t pt-3 mt-3 space-y-2">
                        {linkAccounts.map((account) => (
                          <div
                            key={account.id}
                            className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <Wallet className="w-4 h-4 text-muted-foreground" />
                              <div>
                                <p className="text-sm font-medium">
                                  {account.accountType === "CONTA_DEPOSITO_A_VISTA"
                                    ? "Conta Corrente"
                                    : account.accountType === "CONTA_POUPANCA"
                                    ? "Poupança"
                                    : account.accountType === "CONTA_PAGAMENTO_PRE_PAGA"
                                    ? "Conta de Pagamento"
                                    : "Conta"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Ag. {account.branchCode} • Conta {account.accountNumber}
                                  {account.checkDigit ? `-${account.checkDigit}` : ""}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">
                                {account.cachedAvailableBalance
                                  ? formatCurrency(parseFloat(account.cachedAvailableBalance))
                                  : "—"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {account.cachedBalanceAt
                                  ? `Atualizado ${formatDate(account.cachedBalanceAt)}`
                                  : "Saldo não disponível"}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRefreshAccount(account.id)}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {link.status === "AUTHORISED" && linkAccounts.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Nenhuma conta disponível neste banco.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info sobre Open Finance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Como funciona o Open Finance?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
              1
            </div>
            <div>
              <p className="font-medium text-foreground">Conecte seu banco</p>
              <p>Clique em "Conectar Banco" e autorize o compartilhamento de dados no app do seu banco.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
              2
            </div>
            <div>
              <p className="font-medium text-foreground">Visualize seus saldos</p>
              <p>Acompanhe seus saldos bancários de forma centralizada e identifique dinheiro parado.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
              3
            </div>
            <div>
              <p className="font-medium text-foreground">Faça render no BRLD</p>
              <p>Transfira seu saldo para o BRLD e ganhe 95% do CDI com liquidez imediata (D+0).</p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <p className="text-xs">
              <strong>Segurança:</strong> O Open Finance é regulamentado pelo Banco Central do Brasil.
              Seus dados são compartilhados com segurança e você pode revogar o acesso a qualquer momento.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de revogação */}
      <ConfirmDialog
        open={!!linkToRevoke}
        onClose={() => setLinkToRevoke(null)}
        onConfirm={handleRevokeLink}
        title="Revogar Conexão"
        description={`Tem certeza que deseja desconectar ${linkToRevoke?.participantName || "este banco"}? Você perderá acesso aos dados das contas vinculadas.`}
        variant="danger"
        loading={revoking}
      />
    </div>
  );
}
